<template>
    <div class="orderSummary">
        <p class="orderSummary--title">{{$t('REG_SUMMARY_ORDER_SUMMARY')}}</p>
        <p v-if="hasSuccessfulCoupon" class="orderSummary--coupon-success">{{successText}}</p>
        <div class="orderSummary--flowers">
            <p>{{boxText}}</p>
            <div class="orderSummary--amount">
                <p class="orderSummary--amount--text" :class="{ 'strikeThrough': showDiscountAmount }">{{`${currencySymbol}${getPrice}`}}</p>
                <p class="orderSummary--amount--text" v-if="showDiscountAmount">{{`${currencySymbol}${boxDiscountAmount}`}}</p>
            </div>
        </div>
        <div class="orderSummary--flowers">
            <p>{{$t('REG_SUMMARY_DELIVERY')}}</p>
            <div class="orderSummary--amount">
                <p class="orderSummary--amount--text" :class="{ 'strikeThrough': strikeDeliveryPrice }">{{`${currencySymbol}${shippingPriceText}`}}</p>
                <p class="orderSummary--amount--text" v-if="strikeDeliveryPrice">{{`${currencySymbol}${netShippmentAmount}`}}</p>
            </div>

        </div>
        <div class="orderSummary--flowers">
            <p>{{$t('TOTAL_INCL_VAT')}}</p>
            <div class="orderSummary--amount">
                <p class="orderSummary--amount--text" :class="{ 'strikeThrough': strikeTotalPrice }">{{`${currencySymbol}${totalPriceWithDelivery}`}}</p>
                <p class="orderSummary--total--value" v-if="strikeTotalPrice">{{`${currencySymbol}${netTotalPrice}`}}</p>
            </div>
        </div>
        <h4 class="orderSummary--billing" v-if="!hideBillingText">{{getBillingInfoText}}</h4>
        <p class="orderSummary--offer-code" v-if="showAddOffer" @click="openCouponField">{{$t('REG_SUMMARY_ADD_OFFER_CODE')}}</p>
        <coupon-field class="orderSummary--coupon-field" v-if="showCouponField" :appliedCoupon="couponProperties" :appliedCouponCode="displayDiscountCode" />
    </div>
</template>

<script>

import { mapGetters } from 'vuex';
import CouponField from '@/components/forms/fields/CouponField';
import { PRODUCT_FAMILY_BUMPER, PRODUCT_FAMILY_LITE } from '@/constants/Gifting';

export default {
    name: 'OrderSummary',
    components: {
        CouponField,
    },
    data() {
        return {
            showAddOffer: true,
            hasDeliveryPrice: true,
            hideBillingText: process.env.VUE_APP_FIRST_BOX_PRE_PAY === 'true',
        };
    },
    mounted() {
        this.showAddOffer = !this.hasSuccessfulCoupon;
        this.$store.commit('register/setOrderSummary', {
            box_price: this.getPrice,
            discount_amount: Number(this.boxDiscountAmount) || Number(this.getPrice),
            delivery_price: this.netShippmentAmount,
            total_price: Number(this.netTotalPrice),
        });
    },
    computed: {
        ...mapGetters({
            couponProperties: 'register/getCouponProperties',
            selectedPlan: 'register/getSelectedPlan',
            selectedProductID: 'register/getSelectedProductID',
            products: 'subscription/getProductTypes',
            SelectedBoxName: 'register/getSelectedProductName',
            boxPrice: 'register/getBoxPrice',
            deliveryPrice: 'register/getDeliveryPrice',
            isCanvasserType: 'auth/isCanvasser',
            currencySymbol: 'localisation/getCurrency',
            selectedProductDetails: 'register/getSelectedProductDetails',
        }),
        displayDiscountCode() {
            return this.friendCode;
        },
        boxDiscountAmount() {
            if (this.selectedProductDetails.product_family === PRODUCT_FAMILY_BUMPER) return this.couponProperties.payable_amount_bumper;
            if (this.selectedProductDetails.product_family === PRODUCT_FAMILY_LITE) return this.couponProperties.payable_amount_lite;
            return this.couponProperties?.payable_amount;
        },
        couponPayableAmount() {
            const globalParams = this.$globalQueryParams;
            if (globalParams.np === '1' || globalParams.np === '2') return this.couponProperties?.payable_new_amount;
            return this.couponProperties?.payable_amount;
        },
        showCouponField() {
            return this.hasSuccessfulCoupon || !this.showAddOffer || this.displayDiscountCode;
        },
        hasSuccessfulCoupon() {
            return this.couponProperties?.code && this.couponProperties?.code.length !== 0;
        },
        successText() {
            return this.couponProperties?.registration_text_2 || this.$t('REG_TEXT_2_FALLBACK');
        },
        showDiscountAmount() {
            return this.couponProperties && Number(this.couponProperties.payable_amount) < Number(this.getPrice);
        },
        shippingPrice() {
            if (this.hasDeliveryPrice && this.couponProperties) return this.couponProperties?.payable_amount_delivery || 0;
            return this.hasDeliveryPrice && !this.firstDeliveryFree ? this.deliveryPrice : 0;
        },
        actualShippingPrice() {
            return this.hasDeliveryPrice ? this.deliveryPrice : 0;
        },
        shippingPriceText() {
            return this.shippingPrice === 0 && !this.firstDeliveryFree ? this.$t('REG_SUMMARY_FREE') : this.actualShippingPrice;
        },
        getPrice() {
            return Number(this.selectedProductDetails.price);
        },
        totalPrice() {
            return this.getPrice + this.shippingPrice;
        },
        totalPriceWithDelivery() {
            return this.getPrice + this.deliveryPrice;
        },
        shippmentAmount() {
            return this.hasDeliveryPrice ? this.deliveryPrice || 0 : 0;
        },
        firstDeliveryFree() {
            return this.$globalQueryParams?.fdf !== '0' && !this.isCanvasserType;
        },
        netShippmentAmount() {
            if (this.firstDeliveryFree) return this.$t('REG_SUMMARY_FREE');
            if (this.couponProperties) return this.couponProperties?.payable_amount_delivery || this.$t('REG_SUMMARY_FREE');
            return this.$globalQueryParams.np === '1' || this.$globalQueryParams.np === '2' ? this.$t('REG_SUMMARY_FREE') : this.shippmentAmount;
        },
        orderSummary: {
            get() {
                return this.$store.getters['register/getOrderSummary'];
            },
            set(val) {
                this.$store.commit('register/setOrderSummary', val);
            },
        },
        friendCode: {
            get() {
                return this.$store.getters['register/getFriendCode'];
            },
            set(code) {
                this.$store.commit('register/setFriendCode', code);
            },
        },
        netTotalPrice() {
            if (this.couponProperties) {
                if (PRODUCT_FAMILY_LITE.includes(this.selectedProductDetails.product_family.toLowerCase())) return Number(this.couponProperties.payable_new_amount_lite);
                if (PRODUCT_FAMILY_BUMPER.includes(this.selectedProductDetails.product_family.toLowerCase())) return Number(this.couponProperties.payable_new_amount_bumper);
                return Number(this.couponProperties.payable_new_amount);
            }
            return this.totalPrice.toFixed(2);
        },
        deliveryDay() {
            return this.$store.getters['register/getFirstDeliveryDay'];
        },
        getBillingInfoText() {
            if (this.deliveryDay) {
                return this.$t('REG_SUMMARY_BILLING_INFO_DAY', { day: this.deliveryDay });
            }
            return this.$t('REG_SUMMARY_BILLING_INFO');
        },
        strikeDeliveryPrice() {
            let hasDeliveryPriceOffer = this.hasDeliveryPrice && this.couponProperties && ![0, 4].includes(Number(this.couponProperties?.payable_amount_delivery));
            return this.firstDeliveryFree || hasDeliveryPriceOffer;
        },
        strikeTotalPrice() {
            return this.firstDeliveryFree || this.hasDiscountOnBoxPrice;
        },
        hasDiscountOnBoxPrice() {
            return this.couponProperties && Number(this.couponProperties?.payable_amount) !== Number(this.getPrice);
        },
        boxText() {
            return this.hasDiscountOnBoxPrice ? this.$t('REG_SUMMARY_FIRST_WEEKLY_WITH_DISCOUNT', { plan: this.selectedProductDetails.display_name }) : this.$t('REG_SUMMARY_FIRST_WEEKLY', { plan: this.selectedProductDetails.display_name });
        },
    },
    methods: {
        openCouponField() {
            this.showAddOffer = false;
        },
        capitalizeFirstLetter(value) {
            return value && value.charAt(0).toUpperCase() + value.slice(1);
        },
    },
    watch: {
        netTotalPrice() {
            this.orderSummary = {
                    box_price: this.getPrice,
                    discount_amount: Number(this.boxDiscountAmount) || Number(this.getPrice),
                    delivery_price: this.netShippmentAmount,
                    total_price: Number(this.netTotalPrice),
                };
        },
    },
};
</script>

<style lang="scss" scoped>
.orderSummary{
    max-width: 400px;
    margin: 2rem auto;
    padding: 0 1rem;
    &--title {
        font-family: 'gillsans';
        text-align: center;
        font-size: 21px;
        color: theme('colors.shop.black');
        font-weight: 600;
    }
    &--flowers {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 1rem auto;
        margin-bottom: 0;
        p {
            font-size: 20px;
            margin: 0;
        }
    }
    &--delivery {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0 auto;
        margin-bottom: 1rem;
        p {
            font-size: 20px;
        }
    }
    &--billing {
        font-family: 'gillsans';
        text-align: center;
        font-size: 17px;
        line-height: 22px;
        font-weight: 400;
        color: theme('colors.shop.black');
    }
    &--amount {
        display: flex;
        align-items: center;
        &--text {
            margin: 0 0.25rem;
            font-size: 20px;
            text-align: right;
            font-weight: 400;
            color: theme('colors.shop.black');
        }
        &--text.strikeThrough {
            margin: 0 0.5rem;
            text-decoration: line-through;
            color: theme('colors.shop.darkgrey');
            font-size: 17px;
            font-weight: lighter;
            line-height: 22px;
        }
    }
    &--offer-code {
        text-align: center;
        font-size: 18px;
        font-weight: lighter;
        margin: 1rem auto;
        text-decoration: underline;
        cursor: pointer;
    }
    &--coupon-field {
        margin: 1rem auto;
        padding: 0;
    }
    &--coupon-success {
        text-align: center;
        margin: 1rem auto;
        margin-bottom: 1.5rem;
        font-size: 20px;
        line-height: 25px;
    }
}
</style>
